import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker, ColorPicker, ConfigProvider, Select, Table, Modal, message } from 'antd';
import locale from 'antd/locale/ko_KR';
import { Input, Dropdown, Radio , Button } from '@/components/atoms';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import AcademyStudentTable from '@/components/organisms/Table/AcademyStudentTable';
import StudentSelectionContent from '@/components/Modal/StudentSelectionContent';
import { Icon } from '@/icons';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import { adminTextbookApi, adminLearningScheduleApi } from '@/services/api';

const { RangePicker } = DatePicker;

type Student = {
  id: string;
  userId?: string; // userId 필드 추가
  name: string;
  academy: string;
  grade: string;
  status: 'active' | 'inactive';
  no: number;
  academyCode: string;
  studentName: string;
  birthDate: string;
};

const AdminLearningRegister: React.FC = () => {
  const navigate = useNavigate();

  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [isStudentSelectionModalOpen, setIsStudentSelectionModalOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('#1890ff');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  type TextbookBlock = {
    id: string;
    type: string; // 카테고리
    textbook: string; // 교재 ID
    unitIds: Array<string | number>; // 선택 유닛 ID들(선택값)
    textbookOptions: { value: string; label: string }[]; // 유형 선택 후 로드
    unitOptions: { value: string | number; label: string }[]; // 교재 선택 후 로드
    color?: string; // 유형 디폴트 색상(수정 가능)
  };

  const [textbooks, setTextbooks] = useState<TextbookBlock[]>([
    { id: 'textbook-default', type: '', textbook: '', unitIds: [], textbookOptions: [], unitOptions: [], color: '#1890ff' }
  ]);
  const [maxUnitsPerDay, setMaxUnitsPerDay] = useState<number>(2);
  const [dateRange, setDateRange] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [learningTitle, setLearningTitle] = useState<string>('');
  const [scoringRequired, setScoringRequired] = useState<boolean>(true);

  const typeToDefaultColor = useMemo(() => new Map<string, string>([
    ['파닉스데이', '#1890ff'],
    ['스타터_파닉스', '#2f54eb'],
    ['스타터_뉘앙스', '#722ed1'],
    ['스타터_스트럭처', '#52c41a'],
    ['에세이', '#fa8c16'],
    ['통번역', '#faad14'],
    ['리딩북', '#13c2c2'],
  ]), []);

  // 더미 학생(필요 시 실제 API 연동으로 교체)
  const students: Student[] = [
    { id: 'student1', name: '김철수', academy: '알파 영어 학원 본점', grade: '초등 5학년', status: 'active', no: 1, academyCode: 'ACD-0001', studentName: '김철수', birthDate: '2010-03-15' },
    { id: 'student2', name: '이영희', academy: '알파 영어 학원 본점', grade: '초등 6학년', status: 'active', no: 2, academyCode: 'ACD-0001', studentName: '이영희', birthDate: '2009-07-22' }
  ];

  // value는 백엔드 Textbook.category 값과 동일하게 유지해야 필터가 동작합니다
  const typeOptions = [
    { value: '파닉스데이', label: '파닉스 데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' },
  ];

  // 유형 선택 → 교재 목록 로드
  const loadTextbooksByType = async (idx: number, typeLabel: string) => {
    try {
      const res = await adminTextbookApi.list({ q: '', category: typeLabel, page: 0, size: 100 });
      const data = res?.data || res; const page = data?.data || data; const content = page?.content || [];
      const opts = content.map((t: any) => ({ value: String(t.id), label: t.name }));
      setTextbooks(prev => prev.map((tb, i) => i === idx ? { ...tb, type: typeLabel, textbook: '', unitIds: [], textbookOptions: opts, unitOptions: [], color: typeToDefaultColor.get(typeLabel) || tb.color } : tb));
    } catch (e) { message.error('교재 목록을 불러오지 못했습니다.'); }
  };

  // 교재 선택 → 유닛 목록 로드(선택값은 optional)
  const loadUnitsByTextbook = async (idx: number, textbookId: string) => {
    try {
      const res = await adminTextbookApi.listUnits(textbookId);
      const data = res?.data || res; const list = data?.data || data || [];
      const opts = list.map((u: any) => ({ value: String(u.id), label: u.title }));
      setTextbooks(prev => prev.map((tb, i) => i === idx ? { ...tb, textbook: textbookId, unitOptions: opts } : tb));
    } catch (e) { message.error('유닛 목록을 불러오지 못했습니다.'); }
  };

  const handleLoadStudents = () => setIsStudentSelectionModalOpen(true);
  const handleAddTextbook = () => setTextbooks(prev => [...prev, { id: `textbook-${Date.now()}`, type: '', textbook: '', unitIds: [], textbookOptions: [], unitOptions: [], color: '#1890ff' }]);
  const handleRemoveTextbook = (id: string) => setTextbooks(prev => prev.filter(tb => tb.id !== id));
  const handleTypeChange = (idx: number, typeLabel: string) => { loadTextbooksByType(idx, typeLabel); };
  const handleBookChange = (idx: number, bookId: string) => { loadUnitsByTextbook(idx, bookId); };
  const handleUnitIdsChange = (idx: number, unitIds: (string | number)[]) => setTextbooks(prev => prev.map((tb, i) => i === idx ? { ...tb, unitIds } : tb));

  const buildUnitLinks = () => {
    const dedup = new Map<number, { unitId: number; textbookId?: number }>();
    textbooks.forEach(tb => {
      const numericTextbookId = tb.textbook ? Number(tb.textbook) : undefined;
      const sanitizedTextbookId = numericTextbookId !== undefined && !Number.isNaN(numericTextbookId)
        ? numericTextbookId
        : undefined;
      (tb.unitIds || []).forEach((raw) => {
        const unitId = Number(raw);
        if (Number.isNaN(unitId)) return;
        if (!dedup.has(unitId)) {
          dedup.set(unitId, { unitId, textbookId: sanitizedTextbookId });
        }
      });
    });
    return Array.from(dedup.values());
  };

  // 미리보기 계산: 베이스 최대치 + 미소비 누적 룰
  type PreviewRow = { date: string; capacity: number; assigned: number };
  const calcPreview = (): PreviewRow[] => {
    if (!dateRange || !dateRange?.length) return [];
    const start = new Date(dateRange[0]?.format?.('YYYY-MM-DD') || dateRange[0]);
    const end = new Date(dateRange[1]?.format?.('YYYY-MM-DD') || dateRange[1]);
    const days: PreviewRow[] = [];
    const totalUnitsSelected = textbooks.reduce((sum, tb) => sum + (tb.unitIds?.length || 0) || (tb.textbook ? 0 : 0), 0);
    // 유닛 미선택 시 해당 교재의 전체 유닛 수를 모를 수 있으므로, 미리보기에서는 선택된 유닛 수만 집계
    let remaining = totalUnitsSelected * Math.max(1, selectedStudents.length || 1);
    let carry = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const base = maxUnitsPerDay;
      const capacity = base + carry;
      const assign = Math.min(capacity, remaining);
      remaining -= assign;
      carry = capacity - assign;
      days.push({ date: d.toISOString().slice(0,10), capacity, assigned: assign });
    }
    return days;
  };
  const previewRows = calcPreview();

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        <div className='flex flex-row justify-between items-center pb-5'>
          <h1 className='heading1'>학습등록</h1>
        </div>

        <section>
          <ul>
            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>학습명</p>
              <Input
                placeholder='학습명을 입력하세요'
                className='max-w-[500px]'
                value={learningTitle}
                onChange={(e) => setLearningTitle((e.target as HTMLInputElement).value)}
              />
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>교재선택</p>
              <section className='flex flex-col gap-5 w-full'>
                {textbooks.map((block, index) => (
                  <div key={block.id} className='flex gap-2 items-end w-full'>
                    <div className='flex flex-col gap-2 w-full max-w-[240px]'>
                      <p className='text-sm text-gray-500'>유형</p>
                      <Dropdown
                        placeholder='유형을 선택하세요'
                        options={typeOptions.map(t => ({ value: t.value, label: t.label }))}
                        value={block.type || null}
                        onChange={(v) => handleTypeChange(index, v)}
                      />
                    </div>
                    <div className='flex flex-col gap-2 w-full max-w-[320px]'>
                      <p className='text-sm text-gray-500'>교재</p>
                      <Select
                        placeholder='교재를 선택하세요'
                        value={block.textbook || undefined}
                        onChange={(v) => handleBookChange(index, v)}
                        options={block.textbookOptions}
                        style={{ width: '100%' }}
                        size='large'
                      />
                    </div>
                    <div className='flex flex-col gap-2 w-full max-w-[360px]'>
                      <p className='text-sm text-gray-500'>유닛(선택)</p>
                      <Select
                        mode='multiple'
                        allowClear
                        placeholder='유닛을 선택하세요'
                        value={block.unitIds as any}
                        onChange={(vals) => handleUnitIdsChange(index, vals as any)}
                        options={block.unitOptions}
                        style={{ width: '100%' }}
                        size='large'
                      />
                    </div>
                    {index > 0 && (
                      <Button outline intent='danger' className='flex flex-none gap-2 items-center w-20' onClick={() => handleRemoveTextbook(block.id)}>삭제</Button>
                    )}
                  </div>
                ))}
                <div className='flex gap-2 w-full'>
                  <Button outline className='flex flex-none gap-2 items-center w-40' onClick={handleAddTextbook}>
                    <Icon name='plus' size={20} className='*:fill-indigo-500' />
                    교재 추가하기
                  </Button>
                </div>
              </section>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>일일 최대 학습 가능한 유닛 갯수</p>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-3'>
                  <Select
                    value={String(maxUnitsPerDay)}
                    onChange={(v) => setMaxUnitsPerDay(Number(v))}
                    style={{ width: 120 }}
                    size='large'
                    options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
                  />
                  <span className='text-gray-500'>(1~10, 미소비 수량은 익일 가산)</span>
                </div>
                <p className='text-xs text-gray-500'>
                  하루에 한 학생에게 배정할 수 있는 최대 유닛 수입니다. 예를 들어 3개로 설정하면 당일에는 최대 3개까지만 배분되고 남은 유닛은 다음 날짜로 자동 이월됩니다. {/* [QA Fix] 최대 학습 유닛 설명 */}
                </p>
              </div>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 기간</p>
              <ConfigProvider locale={locale} theme={{ token: { colorPrimary: '#6366f1', borderRadius: 10, fontSize: 16, colorBorder: '#E5E7EB', colorBorderSecondary: '#E5E7EB', colorTextPlaceholder: '#9CA3AF', colorIcon: '#9CA3AF' } }}>
                <div className='flex gap-2 items-center'>
                  <RangePicker format='YYYY-MM-DD' style={{ width: 500, height: 44 }} onChange={(rng) => setDateRange(rng)} />
                </div>
              </ConfigProvider>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>캘린더 색상</p>
              <ConfigProvider locale={locale} theme={{ token: { colorPrimary: '#6366f1', fontSize: 16, colorBorder: '#E5E7EB', colorBorderSecondary: '#E5E7EB', colorTextPlaceholder: '#9CA3AF', colorIcon: '#9CA3AF', controlHeight: 32 } }}>
                <ColorPicker value={selectedColor} onChange={(c) => setSelectedColor(c.toHexString())} size='large' />
              </ConfigProvider>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>채점 여부</p>
              <div className='flex flex-col gap-2'>
                <div className='flex gap-10'>
                  <Radio label='채점함' name='scoring' onChange={() => setScoringRequired(true)} checked={scoringRequired} />
                  <Radio label='채점 불필요' name='scoring' onChange={() => setScoringRequired(false)} checked={!scoringRequired} />
                </div>
                
              </div>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 대상</p>
              <section className='w-full'>
                <div className='flex gap-2 w-full'>
                  <Input placeholder='학습 대상자를 선택해주세요' className='max-w-[500px] w-full' />
                  <Button type='button' className='flex-none' onClick={handleLoadStudents}>불러오기</Button>
                </div>
                <div className='mt-5 max-h-[400px] overflow-y-auto'>
                  <AcademyStudentTable
                    showSearch={false}
                    data={selectedStudents as any}
                  selectedStudents={selectedStudents as any}
                  onStudentSelect={() => {}}
                  onSelectAll={() => {}}
                  type='display'
                  displayType='learning'
                  showPagination={false}
                  onDelete={(student) => {
                    Modal.confirm({
                      title: '학생 삭제',
                      content: '해당 학생을 삭제하시겠습니까?',
                      okText: '삭제',
                      cancelText: '취소',
                      onOk: () => {
                        setSelectedStudents(prev => prev.filter(s => s.id !== student.id));
                        message.success('학생이 삭제되었습니다.');
                      }
                    });
                  }}
                  />
                </div>
              </section>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>등록자</p>
              <p>관리자</p>
            </li>
            <li className='flex py-5 border-b border-gray-100'>
              <p className='flex-none max-w-[150px] w-full text-gray-500'>등록일</p>
              <p>{new Date().toISOString().slice(0,10)}</p>
            </li>
          </ul>
          <div className='flex justify-between my-5 gap-2'>
            <Button outline className='w-28' onClick={() => navigate('/admin/learning')}>뒤로가기</Button>
            <div className='flex gap-2'>
            <Button outline intent='primary' className='w-32' onClick={() => setIsPreviewOpen(true)}>미리보기</Button>
            <Button className='w-28' onClick={async () => {
              try {
                if (!dateRange || !dateRange.length) { message.error('학습 기간을 선택해 주세요'); return; }
                if (selectedStudents.length === 0) { message.error('학습 대상 학생을 선택해 주세요'); return; }
                const academyCodes = Array.from(new Set(selectedStudents.map((s) => s.academyCode).filter(Boolean)));
                if (academyCodes.length === 0) { message.error('학생의 학원 정보를 찾을 수 없습니다.'); return; }
                if (academyCodes.length > 1) { message.error('단일 학원 학생만 선택해 주세요.'); return; }
                const academyCode = academyCodes[0] as string;
                const academyIds = Array.from(new Set(selectedStudents.map((s: any) => s.academyId).filter(Boolean)));
                const academyId = (academyIds.length > 0 ? academyIds[0] : 0) as number;
                const startStr = dateRange[0]?.format?.('YYYY-MM-DD') || String(dateRange[0]);
                const endStr = dateRange[1]?.format?.('YYYY-MM-DD') || String(dateRange[1]);
                // 학생별 스케줄 저장
                const items = selectedStudents.map(s => ({
                  id: null,
                  studentUserId: Number(s.userId || s.id) || undefined,
                  title: (learningTitle || '').trim() || '학습',
                  startDate: startStr,
                  endDate: endStr,
                  backgroundColor: selectedColor,
                  borderColor: selectedColor,
                  maxUnitsPerDay: maxUnitsPerDay,
                  scoringRequired
                }));
                
                // 선택된 유닛 정보
                const unitLinks = buildUnitLinks();
                const unitIds = unitLinks.map(link => link.unitId);
                if (unitIds.length === 0) {
                  message.error('배정할 유닛을 선택해 주세요.');
                  return;
                }

                // 스케줄 저장
                const saved = await adminLearningScheduleApi.save({ academyId, academyCode, items, unitIds, unitLinks });
                const body = saved?.data || saved;
                const schedules = body?.data?.schedules || body?.schedules || [];
                
                message.success('학습이 저장되었습니다.');
                navigate('/admin/learning');
              } catch (e) {
                message.error('학습 저장에 실패했습니다.');
              }
            }}>저장</Button>
            </div>
          </div>
        </section>

        {/* 학생 선택 다이얼로그 */}
        <Modal
          open={isStudentSelectionModalOpen}
          onCancel={() => setIsStudentSelectionModalOpen(false)}
          footer={null}
          width={800}
          destroyOnClose
          maskClosable={false}
          bodyStyle={{ padding: 0 }}
        >
          <StudentSelectionContent
            onOk={(sel) => {
              setSelectedStudents(prev => {
                const map = new Map<string, any>();
                [...prev, ...(sel as any[])].forEach((s: any) => { map.set(String(s.id), s); });
                return Array.from(map.values());
              });
              setIsStudentSelectionModalOpen(false);
            }}
            onCancel={() => setIsStudentSelectionModalOpen(false)}
            title='학생 선택'
            useAdminApi
          />
        </Modal>

        {/* 학습 프로세스 미리보기 다이얼로그 */}
        <Dialog
          open={isPreviewOpen}
          onCancel={() => setIsPreviewOpen(false)}
          onOk={() => setIsPreviewOpen(false)}
          okText='닫기'
          className='!max-w-[900px] !w-full'
        >
          <div className='p-5'>
            <div className='text-base font-semibold mb-3'>학습 프로세스 미리보기 (누적 최대치 가산)</div>
            <Table
              size='small'
              pagination={false}
              rowKey='date'
              dataSource={previewRows}
              columns={[
                { title: '날짜', dataIndex: 'date' },
                { title: '당일 허용치', dataIndex: 'capacity' },
                { title: '배정 수', dataIndex: 'assigned' }
              ]}
            />
            <div className='text-xs text-gray-500 mt-2'>유닛 미선택 교재는 전체 유닛 배정(프리뷰 미포함). 저장 시 실제 배정 로직과 일치하도록 서버 연동 예정.</div>
          </div>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLearningRegister;


